import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowUpCircle,
  Building2,
  CircleDot,
  CircleOff,
  Clock,
  MonitorPlay,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';
import { fetchDashboard } from '@/lib/api/endpoints/dashboard';
import { queryKeys } from '@/lib/query-keys';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/Feedback';
import { formatNumber, formatRelative, humaniseAction } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { AttentionReason, AuditEvent, DashboardSummary } from '@/lib/api/types';

/*
  Dashboard.

  Layout intent: the four numbers a technician needs before they do anything sit on one row
  at the top, then two columns, attention on the left because it is actionable and activity
  on the right because it is context.

  The online tile counts every connected state, not only the literal online presence value,
  and says so in its own caption. Two numbers that look like they should agree and do not is
  the fastest way to lose trust in a dashboard, so the definition is on the tile rather than
  in documentation nobody opens.
*/

interface TileProps {
  readonly label: string;
  readonly value: number;
  readonly caption: string;
  readonly Icon: typeof CircleDot;
  readonly tone: 'online' | 'offline' | 'session' | 'neutral';
}

const TILE_TONES: Readonly<Record<TileProps['tone'], string>> = {
  online: 'text-[var(--color-status-online)] bg-[var(--color-status-online-soft)]',
  offline: 'text-[var(--color-status-offline)] bg-[var(--color-status-offline-soft)]',
  session: 'text-[var(--color-status-session)] bg-[var(--color-status-session-soft)]',
  neutral: 'text-fg-muted bg-surface-sunken',
};

/**
 * Tile contents only. The link is applied by the caller, because TanStack Router types the
 * destination and its search parameters together and a generic "to: string" prop would throw
 * that checking away.
 */
function TileBody({ label, value, caption, Icon, tone }: TileProps): React.JSX.Element {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{label}</p>
        <span
          className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', TILE_TONES[tone])}
        >
          <Icon className="size-4" aria-hidden="true" />
        </span>
      </div>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-fg tabular-nums">
        {formatNumber(value)}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-fg-subtle">{caption}</p>
    </>
  );
}

const TILE_LINK_CLASS = 'block h-full rounded-[var(--radius-panel)] p-4';
const TILE_CARD_CLASS = 'transition-colors hover:border-border-strong';

const ATTENTION_META: Readonly<
  Record<AttentionReason, { readonly label: string; readonly Icon: typeof ArrowUpCircle }>
> = {
  update_required: { label: 'Update required', Icon: ArrowUpCircle },
  agent_outdated: { label: 'Agent outdated', Icon: ArrowUpCircle },
  offline_extended: { label: 'Offline too long', Icon: CircleOff },
  enrollment_pending: { label: 'Enrollment incomplete', Icon: Clock },
  reboot_pending: { label: 'Reboot pending', Icon: TriangleAlert },
};

const SEVERITY_TONE = {
  info: 'neutral',
  notice: 'info',
  warning: 'warning',
  critical: 'danger',
} as const;

function ActivityRow({ event }: { readonly event: AuditEvent }): React.JSX.Element {
  return (
    <li className="flex items-start gap-3 border-b border-border px-4 py-2.5 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-fg">{humaniseAction(event.action)}</span>
          {event.outcome !== 'success' ? (
            <Badge tone={event.outcome === 'denied' ? 'warning' : 'danger'}>
              {event.outcome === 'denied' ? 'Denied' : 'Failed'}
            </Badge>
          ) : null}
          <Badge tone={SEVERITY_TONE[event.severity]}>{event.severity}</Badge>
        </div>
        <p className="mt-0.5 truncate text-xs text-fg-muted">
          {event.actorName ?? 'System'}
          {event.targetLabel === null ? '' : ` on ${event.targetLabel}`}
          {event.organizationName === null ? '' : ` at ${event.organizationName}`}
        </p>
      </div>
      <time
        dateTime={event.occurredAt}
        title={event.occurredAt}
        className="shrink-0 text-xs whitespace-nowrap text-fg-subtle"
      >
        {formatRelative(event.occurredAt)}
      </time>
    </li>
  );
}

function LoadingGrid(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <Spinner label="Loading dashboard" />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((index) => (
          <Card key={index} className="p-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-4 h-8 w-16" />
            <Skeleton className="mt-2 h-3 w-full" />
          </Card>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      </div>
    </div>
  );
}

function Content({ data }: { readonly data: DashboardSummary }): React.JSX.Element {
  const sessionCount = data.presenceBreakdown.session_active;
  const needingUpdate = data.presenceBreakdown.update_required;

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="fleet-heading" className="flex flex-col gap-3">
        <h2 id="fleet-heading" className="sr-only">
          Fleet summary
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card className={TILE_CARD_CLASS}>
            <Link
              to="/devices"
              search={{ states: 'online,session_active,rebooting,update_required' }}
              className={TILE_LINK_CLASS}
            >
              <TileBody
                label="Devices online"
                value={data.devicesOnline}
                caption={`Connected to a gateway now. Includes ${String(sessionCount)} in a session and ${String(needingUpdate)} awaiting an update.`}
                Icon={CircleDot}
                tone="online"
              />
            </Link>
          </Card>

          <Card className={TILE_CARD_CLASS}>
            <Link to="/devices" search={{ states: 'offline' }} className={TILE_LINK_CLASS}>
              <TileBody
                label="Devices offline"
                value={data.devicesOffline}
                caption={`Out of ${formatNumber(data.devicesTotal)} enrolled endpoints.`}
                Icon={CircleOff}
                tone="offline"
              />
            </Link>
          </Card>

          <Card className={TILE_CARD_CLASS}>
            <Link to="/sessions" className={TILE_LINK_CLASS}>
              <TileBody
                label="Active sessions"
                value={data.sessionsActive}
                caption={`${formatNumber(data.sessionsToday)} sessions recorded today.`}
                Icon={MonitorPlay}
                tone="session"
              />
            </Link>
          </Card>

          <Card className={TILE_CARD_CLASS}>
            <Link to="/organizations" className={TILE_LINK_CLASS}>
              <TileBody
                label="Organizations"
                value={data.organizationsActive}
                caption="Active customers under this tenant."
                Icon={Building2}
                tone="neutral"
              />
            </Link>
          </Card>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Devices needing attention"
            description="Ordered by how long the condition has persisted."
            action={
              <Badge tone={data.needsAttention.length === 0 ? 'success' : 'warning'}>
                {formatNumber(data.needsAttention.length)}
              </Badge>
            }
          />
          {data.needsAttention.length === 0 ? (
            <EmptyState
              title="Nothing needs attention"
              description="Every enrolled endpoint is current and reachable."
            />
          ) : (
            <ul>
              {data.needsAttention.map((item) => {
                const meta = ATTENTION_META[item.reason];
                const Icon = meta.Icon;
                return (
                  <li
                    key={`${item.deviceId}-${item.reason}`}
                    className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0"
                  >
                    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[var(--color-warning-soft)] text-[var(--color-warning)]">
                      <Icon className="size-3.5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/*
                          Straight to the device, now that there is a detail page to land on.
                          This used to search the list for the hostname, which was the best
                          available when the only link was a fragment anchor.
                        */}
                        <Link
                          to="/devices/$deviceId"
                          params={{ deviceId: item.deviceId }}
                          className="rounded font-mono text-sm font-medium text-accent hover:underline"
                        >
                          {item.deviceName}
                        </Link>
                        <Badge tone="warning">{meta.label}</Badge>
                      </div>
                      <p className="mt-0.5 text-xs text-fg-muted">{item.detail}</p>
                      <p className="mt-0.5 text-xs text-fg-subtle">
                        {item.organizationName} &middot; since{' '}
                        <time dateTime={item.since} title={item.since}>
                          {formatRelative(item.since)}
                        </time>
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Recent activity"
            description="From the append-only audit log. Denials are recorded alongside successes."
            action={
              <Link
                to="/audit"
                className="rounded text-xs font-semibold text-accent hover:underline"
              >
                View audit log
              </Link>
            }
          />
          {data.recentActivity.length === 0 ? (
            <EmptyState title="No recent activity" />
          ) : (
            <ul>
              {data.recentActivity.map((event) => (
                <ActivityRow key={event.id} event={event} />
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

export function DashboardPage(): React.JSX.Element {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: fetchDashboard,
    // Presence changes constantly. A 30 second refresh is honest without hammering the API.
    refetchInterval: 30_000,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        description="Fleet presence, live sessions, and what needs a technician right now."
      />

      {isError ? (
        <Alert tone="danger" title="Could not load the dashboard" liveness="assertive">
          <p>{error instanceof Error ? error.message : 'Unknown error.'}</p>
          <button
            type="button"
            onClick={() => {
              void refetch();
            }}
            className="mt-2 rounded font-semibold underline"
          >
            Try again
          </button>
        </Alert>
      ) : null}

      {isPending ? <LoadingGrid /> : data === undefined ? null : <Content data={data} />}

      <p className="flex items-start gap-2 text-xs text-fg-subtle">
        <ShieldAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Counts reflect what this account is authorized to see. Scope is resolved server
          side per request, so a narrower assignment produces smaller numbers here without
          any indication that other devices exist.
        </span>
      </p>
    </div>
  );
}
